import React from 'react';
import type { PropertyAnalysisResult } from '../../types/calculations';
import { SectionTitle, GoldBadge } from '../Luxury';
import { Users, Home, Briefcase, ArrowUpRight, ArrowRight, ArrowDownRight } from 'lucide-react';

interface Props {
  data: PropertyAnalysisResult;
}

const getGradeColor = (grade: string): string => {
  if (grade.startsWith('A')) return 'text-emerald-400';
  if (grade.startsWith('B')) return 'text-blue-400';
  if (grade.startsWith('C')) return 'text-amber-400';
  if (grade.startsWith('D')) return 'text-orange-400';
  return 'text-red-400';
};

const getTrajectoryConfig = (trajectory: string) => {
  switch (trajectory) {
    case 'improving':
      return { icon: <ArrowUpRight className="w-4 h-4" />, color: 'text-emerald-400', bg: 'bg-emerald-500/20 border-emerald-500/40', label: 'Improving' };
    case 'declining':
      return { icon: <ArrowDownRight className="w-4 h-4" />, color: 'text-red-400', bg: 'bg-red-500/20 border-red-500/40', label: 'Declining' };
    default:
      return { icon: <ArrowRight className="w-4 h-4" />, color: 'text-amber-400', bg: 'bg-amber-500/20 border-amber-500/40', label: 'Stable' };
  }
};

interface SubScoreCardProps {
  icon: React.ReactNode;
  title: string;
  score: number;
  grade: string;
  narrative: string;
  components: Record<string, number>;
}

const SubScoreCard: React.FC<SubScoreCardProps> = ({ icon, title, score, grade, narrative, components }) => (
  <div className="bg-graphite rounded-lg border border-slate-dark p-4">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <span className="text-stone-400">{icon}</span>
        <span className="text-sm font-semibold text-stone-300">{title}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-lg font-bold font-mono ${getGradeColor(grade)}`}>{grade}</span>
        <span className="text-sm text-stone-400 font-mono">{score.toFixed(0)}/100</span>
      </div>
    </div>
    <p className="text-sm text-stone-400 mb-3 leading-relaxed">{narrative}</p>
    {Object.keys(components).length > 0 && (
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(components).map(([name, value]) => (
          <div key={name} className="flex justify-between text-sm px-2 py-1 bg-charcoal/50 rounded">
            <span className="text-stone-400 truncate">{name.replace(/_/g, ' ')}</span>
            <span className="font-mono text-stone-300 ml-2">{typeof value === 'number' ? value.toFixed(1) : value}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const DemographicInsightsCard: React.FC<Props> = ({ data }) => {
  const profile = data.investment_intelligence?.demographic_profile;

  if (!profile) {
    return (
      <div className="bg-charcoal rounded-xl border border-slate-dark p-6">
        <SectionTitle icon={<Users className="w-5 h-5" />}>Demographic Insights</SectionTitle>
        <p className="text-sm text-stone-400 text-center py-8">
          Demographic profile data not available for this property.
        </p>
      </div>
    );
  }

  const trajectory = getTrajectoryConfig(profile.neighborhood_trajectory);

  return (
    <div className="bg-charcoal rounded-xl border border-slate-dark p-6">
      <div className="flex items-center justify-between mb-6">
        <SectionTitle icon={<Users className="w-5 h-5" />}>Demographic Insights</SectionTitle>
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-semibold uppercase tracking-wider ${trajectory.bg} ${trajectory.color}`}>
            {trajectory.icon}
            {trajectory.label}
          </div>
          <GoldBadge variant="gold">
            {profile.composite_grade} ({profile.composite_score.toFixed(0)})
          </GoldBadge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SubScoreCard
          icon={<Users className="w-4 h-4" />}
          title="Tenant Quality"
          score={profile.tenant_quality.score}
          grade={profile.tenant_quality.grade}
          narrative={profile.tenant_quality.narrative}
          components={profile.tenant_quality.components}
        />
        <SubScoreCard
          icon={<Home className="w-4 h-4" />}
          title="Affordability Ceiling"
          score={profile.affordability_ceiling.score}
          grade={profile.affordability_ceiling.grade}
          narrative={profile.affordability_ceiling.narrative}
          components={profile.affordability_ceiling.components}
        />
        <SubScoreCard
          icon={<Briefcase className="w-4 h-4" />}
          title="Employment Diversity"
          score={profile.employment_diversity.score}
          grade={profile.employment_diversity.grade}
          narrative={profile.employment_diversity.narrative}
          components={profile.employment_diversity.components}
        />
      </div>
    </div>
  );
};
